import type { OpportunityKind } from 'definitions';
import type { LearningsPageResponse, PageOpportunity } from '@@/api/learnings/page/page-types';
import type { LearningsPageManager } from '@/state/learnings/useLearningsPage';
import type { LearningsScreenManager } from '@/state/learnings/useLearningsScreen';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { cn } from 'helpers/cn';
import { mechanismLabel } from '@/state/experiments/expected-mechanisms';
import {
  changeTypeLabel,
  OPPORTUNITY_FILTERS,
  OPPORTUNITY_KIND_LABELS,
  OPPORTUNITY_KIND_TONE,
  opportunityWhere,
  pageLabel,
} from '@/state/learnings/page-view';
import { EmptyState, FOCUS_RING, Panel, PillTabs } from '../../ConsolePanel';
import { Section, TagPill, ToneChip } from './parts';

const CARRIES = [
  'Hypothesis written from the open question',
  'Expected mechanism',
  'Page targeting from the tests behind it',
  'Links to the contributing experiments',
  'The current estimate, as the baseline to beat',
];

type OpportunitiesViewProps = {
  manager: LearningsPageManager;
  page: LearningsPageResponse;
  screen: LearningsScreenManager;
};

/** The open questions the evidence raises, each with the experiment that would answer it. */
export const OpportunitiesView = ({ manager, page, screen }: OpportunitiesViewProps) => {
  const [filter, setFilter] = useState<OpportunityKind | 'all'>('all');
  const shown = page.opportunities.filter((opportunity) => filter === 'all' || opportunity.kind === filter);

  return (
    <div className="flex flex-col gap-[14px]">
      <div className="flex flex-col gap-1">
        <h1 className="m-0 text-[18px] font-medium tracking-[-0.01em]">Opportunities</h1>
        <p className="supporting m-0 text-meta">
          Open questions the evidence raises, and the experiment that would answer each one.
        </p>
      </div>
      <div className="flex flex-wrap items-start gap-[14px]">
        <div className="flex min-w-0 flex-[2.4_1_520px] flex-col gap-[14px]">
          <PillTabs
            options={OPPORTUNITY_FILTERS}
            value={filter}
            onChange={(value) => setFilter(value as OpportunityKind | 'all')}
          />
          {shown.map((opportunity, index) => (
            <OpportunityCard
              key={opportunity.id}
              opportunity={opportunity}
              manager={manager}
              screen={screen}
              tourAnchor={index === 0 ? 'learnings-opportunity' : undefined}
            />
          ))}
          {shown.length === 0 && <EmptyState>No open questions of this kind right now.</EmptyState>}
        </div>
        <div className="flex min-w-0 flex-[1_1_260px] flex-col gap-[14px]">
          <Panel
            title="What “Draft experiment” carries over"
            description="The new experiment opens pre-filled, so the reasoning travels with it."
          >
            <ul className="m-0 flex list-none flex-col p-0">
              {CARRIES.map((line) => (
                <li key={line} className="flex items-start gap-2 py-1.5 text-meta">
                  <Check className="text-ok mt-px size-[13px] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                  {line}
                </li>
              ))}
            </ul>
          </Panel>
          <Untested page={page} onOpenGrid={() => screen.openTab('evidence')} />
        </div>
      </div>
    </div>
  );
};

type OpportunityCardProps = {
  opportunity: PageOpportunity;
  manager: LearningsPageManager;
  screen: LearningsScreenManager;
  tourAnchor?: string;
};

const OpportunityCard = ({ opportunity, manager, screen, tourAnchor }: OpportunityCardProps) => {
  const tone = OPPORTUNITY_KIND_TONE[opportunity.kind];
  const source = opportunity.pattern
    ? { label: opportunity.pattern.label, open: () => screen.openPattern(opportunity.pattern!.key) }
    : opportunity.experimentId
      ? { label: `Test #${opportunity.experimentId}`, open: () => screen.openTest(opportunity.experimentId!) }
      : { label: 'Untested combinations', open: () => screen.openTab('evidence') };
  const mechanism = opportunity.mechanism ? mechanismLabel(opportunity.mechanism) : null;
  const drafting = manager.draftingId === opportunity.id;
  const [confirming, setConfirming] = useState(false);

  return (
    <section className="bg-panel border-line rounded-panel overflow-hidden border" data-tour={tourAnchor}>
      <div className="flex flex-col gap-2 p-3.5">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-1.5">
            <ToneChip tone={tone}>{OPPORTUNITY_KIND_LABELS[opportunity.kind]}</ToneChip>
            <TagPill>{opportunityWhere(opportunity)}</TagPill>
          </div>
          <span className="supporting">
            From:{' '}
            <button type="button" onClick={source.open} className={cn('text-accent hover:underline', FOCUS_RING)}>
              {source.label}
            </button>
          </span>
        </div>
        <h2 className="m-0 text-body font-medium leading-snug text-balance">{opportunity.question}</h2>
        <p className="text-dim m-0 max-w-[80ch] text-meta text-pretty">{opportunity.explanation}</p>
        {opportunity.kind === 'fill-gap' && opportunity.changeTypes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {opportunity.changeTypes.map((changeType) => (
              <TagPill key={changeType}>{changeTypeLabel(changeType)}</TagPill>
            ))}
          </div>
        )}
        <div className="mt-1 grid grid-cols-2 gap-[7px]">
          <Box label="Suggested experiment">{opportunity.suggestedExperiment}</Box>
          <Box label="Expected mechanism">{mechanism ?? 'None named: pick one when you draft it'}</Box>
        </div>
      </div>
      <div className="bg-panel2 border-line flex justify-end gap-2 border-t px-3.5 py-2.5">
        <Button size="sm" variant="ghost" onClick={() => manager.dismiss(opportunity.id)}>
          Dismiss
        </Button>
        {manager.canDraft && (
          <Button size="sm" disabled={drafting} onClick={() => setConfirming(true)}>
            {drafting ? 'Drafting...' : 'Draft experiment'}
          </Button>
        )}
      </div>
      {/* Asked first (decided 2026-10-20): a draft is a new experiment in the website's Library. */}
      <ConfirmDialog
        open={confirming}
        title="Create a draft experiment?"
        description="It is added to this website's Library as a draft. Nothing runs until someone starts it."
        confirmLabel="Create draft"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          manager.draft(opportunity.id);
        }}
      >
        <div className="flex flex-col gap-2.5">
          <p className="m-0 text-body font-medium text-balance">{opportunity.question}</p>
          <span className="micro-label">It opens pre-filled with</span>
          <ul className="m-0 flex list-none flex-col p-0">
            {CARRIES.map((line) => (
              <li key={line} className="flex items-start gap-2 py-1 text-meta">
                <Check className="text-ok mt-px size-[13px] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </ConfirmDialog>
    </section>
  );
};

type BoxProps = { label: string; children: string };

const Box = ({ label, children }: BoxProps) => (
  <div className="bg-panel2 border-line flex flex-col gap-1 rounded-card border px-3 py-2.5">
    <span className="micro-label">{label}</span>
    <span className="text-meta">{children}</span>
  </div>
);

type UntestedProps = { page: LearningsPageResponse; onOpenGrid: () => void };

const Untested = ({ page, onOpenGrid }: UntestedProps) => (
  <Section
    tourAnchor="learnings-untested"
    title="Untested combinations"
    description="Gaps in the page by change type grid."
  >
    {page.untested.slice(0, 8).map((entry) => (
      <div key={entry.changeType} className="border-line flex justify-between gap-2.5 border-b px-3.5 py-2 text-meta">
        <span className="font-medium">{changeTypeLabel(entry.changeType)}</span>
        <span className="text-dim text-right">{entry.pageTypes.map(pageLabel).join(', ')}</span>
      </div>
    ))}
    {page.untested.length === 0 && (
      <p className="supporting m-0 px-3.5 py-2">No gaps: every change type tried here was tried on every page.</p>
    )}
    <div className="px-3.5 py-2.5">
      <button type="button" onClick={onOpenGrid} className={cn('text-accent text-meta hover:underline', FOCUS_RING)}>
        Open the grid
      </button>
    </div>
  </Section>
);
